import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../config'

/**
 * PreloadScene - Loads all game assets
 * Shows a loading progress bar while assets load
 */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PreloadScene' })
  }

  preload(): void {
    this.createLoadingBar()
    this.loadAssets()
  }

  private createLoadingBar(): void {
    const centerX = GAME_WIDTH / 2
    const centerY = GAME_HEIGHT / 2

    // Loading text
    const loadingText = this.add.text(centerX, centerY - 50, 'Loading...', {
      fontFamily: 'system-ui',
      fontSize: '24px',
      color: '#ffffff',
    })
    loadingText.setOrigin(0.5)

    // Progress bar background
    const progressBarBg = this.add.rectangle(
      centerX,
      centerY,
      400,
      30,
      0x333333
    )
    progressBarBg.setStrokeStyle(2, 0xffffff)

    // Progress bar fill
    const progressBar = this.add.rectangle(
      centerX - 195,
      centerY,
      0,
      22,
      0xf59e0b // amber-500
    )
    progressBar.setOrigin(0, 0.5)

    // Percentage text
    const percentText = this.add.text(centerX, centerY + 50, '0%', {
      fontFamily: 'system-ui',
      fontSize: '18px',
      color: '#ffffff',
    })
    percentText.setOrigin(0.5)

    // Update progress bar as assets load
    this.load.on('progress', (value: number) => {
      progressBar.width = 390 * value
      percentText.setText(`${Math.round(value * 100)}%`)
    })

    this.load.on('complete', () => {
      loadingText.setText('Complete!')
      // Create placeholders for any assets that failed to load
      this.createPlaceholderAssets()
    })
  }

  private loadAssets(): void {
    // ============================================
    // Character: Marmelade
    // ============================================
    this.load.image('marmelade_waving', 'assets/sprites/character/marmelade_waving.png')
    this.load.image('marmelade_idle', 'assets/sprites/character/marmelade_idle.png')
    this.load.image('marmelade_happy', 'assets/sprites/character/marmelade_happy.png')
    this.load.image('marmelade_excited', 'assets/sprites/character/marmelade_excited.png')
    this.load.image('marmelade_encouraging', 'assets/sprites/character/marmelade_encouraging.png')

    // ============================================
    // Ingredients (future)
    // ============================================
    // this.load.image('strawberry', 'assets/sprites/ingredients/strawberry.png')

    // ============================================
    // Kitchen items (future)
    // ============================================
    // this.load.image('cutting_board', 'assets/sprites/kitchen/cutting_board.png')
    // this.load.image('pot', 'assets/sprites/kitchen/pot.png')

    // ============================================
    // Audio (future)
    // ============================================
    // this.load.audio('bgm_menu', 'assets/audio/bgm/menu.mp3')
    // this.load.audio('sfx_chop', 'assets/audio/sfx/chop.wav')
  }

  private createPlaceholderAssets(): void {
    // Create simple colored rectangles as placeholder graphics
    // These will be replaced with real sprites later

    // Placeholder for Marmelade character (if images not loaded)
    if (!this.textures.exists('marmelade_waving')) {
      this.createCharacterPlaceholder('marmelade_waving')
    }
    if (!this.textures.exists('marmelade_idle')) {
      this.createCharacterPlaceholder('marmelade_idle')
    }
    if (!this.textures.exists('marmelade_happy')) {
      this.createCharacterPlaceholder('marmelade_happy')
    }
    if (!this.textures.exists('marmelade_excited')) {
      this.createCharacterPlaceholder('marmelade_excited')
    }
    if (!this.textures.exists('marmelade_encouraging')) {
      this.createCharacterPlaceholder('marmelade_encouraging')
    }

    // Placeholder for ingredients (colored circles)
    const colors = [0xff6b6b, 0x4ecdc4, 0xffe66d, 0x95e1d3, 0xf38181]
    colors.forEach((color, index) => {
      const graphics = this.make.graphics({ x: 0, y: 0 })
      graphics.fillStyle(color)
      graphics.fillCircle(32, 32, 30)
      graphics.generateTexture(`ingredient_${index}`, 64, 64)
      graphics.destroy()
    })

    // Placeholder for pot/bowl
    const potGraphics = this.make.graphics({ x: 0, y: 0 })
    potGraphics.fillStyle(0x8b4513)
    potGraphics.fillRoundedRect(10, 20, 140, 100, 20)
    potGraphics.fillStyle(0xa0522d)
    potGraphics.fillRoundedRect(0, 10, 160, 30, 10)
    potGraphics.generateTexture('pot', 160, 130)
    potGraphics.destroy()

    // Placeholder for spatula/utensil
    const spatulaGraphics = this.make.graphics({ x: 0, y: 0 })
    spatulaGraphics.fillStyle(0xdeb887)
    spatulaGraphics.fillRoundedRect(15, 0, 20, 120, 5)
    spatulaGraphics.fillStyle(0x808080)
    spatulaGraphics.fillRoundedRect(5, 100, 40, 50, 8)
    spatulaGraphics.generateTexture('spatula', 50, 150)
    spatulaGraphics.destroy()

    // Chopping mini-game assets
    this.createChoppingAssets()
  }

  private createChoppingAssets(): void {
    // Cutting board
    const boardGraphics = this.make.graphics({ x: 0, y: 0 })
    boardGraphics.fillStyle(0xdeb887) // Light wood
    boardGraphics.fillRoundedRect(0, 0, 300, 180, 15)
    boardGraphics.fillStyle(0xc4a574) // Wood grain lines
    for (let i = 0; i < 5; i++) {
      boardGraphics.fillRect(10 + i * 60, 10, 3, 160)
    }
    boardGraphics.lineStyle(4, 0xa0845c)
    boardGraphics.strokeRoundedRect(0, 0, 300, 180, 15)
    boardGraphics.generateTexture('cutting_board', 300, 180)
    boardGraphics.destroy()

    // Strawberry (whole)
    const strawberryGraphics = this.make.graphics({ x: 0, y: 0 })
    // Body
    strawberryGraphics.fillStyle(0xff4444)
    strawberryGraphics.fillTriangle(32, 8, 8, 56, 56, 56)
    strawberryGraphics.fillCircle(32, 40, 24)
    // Leaves
    strawberryGraphics.fillStyle(0x4caf50)
    strawberryGraphics.fillTriangle(32, 0, 24, 16, 40, 16)
    strawberryGraphics.fillTriangle(24, 4, 16, 18, 32, 14)
    strawberryGraphics.fillTriangle(40, 4, 32, 14, 48, 18)
    // Seeds
    strawberryGraphics.fillStyle(0xffeb3b)
    const seedPositions = [
      [24, 35], [40, 35], [32, 45], [20, 48], [44, 48], [28, 55], [36, 55]
    ]
    seedPositions.forEach(([sx, sy]) => {
      strawberryGraphics.fillEllipse(sx, sy, 3, 4)
    })
    strawberryGraphics.generateTexture('strawberry', 64, 64)
    strawberryGraphics.destroy()

    // Strawberry half (left)
    const leftHalfGraphics = this.make.graphics({ x: 0, y: 0 })
    leftHalfGraphics.fillStyle(0xff4444)
    leftHalfGraphics.fillTriangle(32, 8, 8, 56, 32, 56)
    leftHalfGraphics.fillStyle(0xff6b6b) // Inner flesh
    leftHalfGraphics.fillTriangle(30, 14, 14, 52, 30, 52)
    leftHalfGraphics.fillStyle(0x4caf50)
    leftHalfGraphics.fillTriangle(32, 0, 24, 16, 32, 16)
    leftHalfGraphics.fillTriangle(24, 4, 16, 18, 32, 14)
    leftHalfGraphics.generateTexture('strawberry_half_left', 36, 64)
    leftHalfGraphics.destroy()

    // Strawberry half (right)
    const rightHalfGraphics = this.make.graphics({ x: 0, y: 0 })
    rightHalfGraphics.fillStyle(0xff4444)
    rightHalfGraphics.fillTriangle(4, 8, 4, 56, 28, 56)
    rightHalfGraphics.fillStyle(0xff6b6b) // Inner flesh
    rightHalfGraphics.fillTriangle(6, 14, 6, 52, 22, 52)
    rightHalfGraphics.fillStyle(0x4caf50)
    rightHalfGraphics.fillTriangle(4, 0, 4, 16, 12, 16)
    rightHalfGraphics.fillTriangle(12, 4, 4, 14, 20, 18)
    rightHalfGraphics.generateTexture('strawberry_half_right', 36, 64)
    rightHalfGraphics.destroy()

    // Stirring mini-game assets
    this.createStirringAssets()
  }

  private createStirringAssets(): void {
    // Pot (top-down view)
    const potGraphics = this.make.graphics({ x: 0, y: 0 })
    // Outer rim
    potGraphics.fillStyle(0x4a4a4a)
    potGraphics.fillCircle(75, 75, 75)
    // Inner pot
    potGraphics.fillStyle(0x3a3a3a)
    potGraphics.fillCircle(75, 75, 65)
    // Handles
    potGraphics.fillStyle(0x4a4a4a)
    potGraphics.fillRoundedRect(0, 60, 20, 30, 5)
    potGraphics.fillRoundedRect(130, 60, 20, 30, 5)
    // Highlight
    potGraphics.fillStyle(0x6a6a6a)
    potGraphics.fillCircle(55, 55, 15)
    potGraphics.generateTexture('pot_topdown', 150, 150)
    potGraphics.destroy()

    // Wooden spoon
    const spoonGraphics = this.make.graphics({ x: 0, y: 0 })
    // Handle
    spoonGraphics.fillStyle(0xdeb887)
    spoonGraphics.fillRoundedRect(12, 0, 16, 80, 4)
    // Spoon head
    spoonGraphics.fillStyle(0xc4a574)
    spoonGraphics.fillEllipse(20, 95, 18, 25)
    // Highlight on handle
    spoonGraphics.fillStyle(0xf5deb3)
    spoonGraphics.fillRect(14, 5, 4, 70)
    spoonGraphics.generateTexture('spoon', 40, 120)
    spoonGraphics.destroy()

    // Jarring mini-game assets
    this.createJarringAssets()
  }

  private createJarringAssets(): void {
    // Pot (side view for pouring)
    const potSideGraphics = this.make.graphics({ x: 0, y: 0 })
    // Pot body
    potSideGraphics.fillStyle(0x4a4a4a)
    potSideGraphics.fillRoundedRect(10, 30, 80, 50, { tl: 5, tr: 5, bl: 20, br: 20 })
    // Handle
    potSideGraphics.fillStyle(0x3a3a3a)
    potSideGraphics.fillRoundedRect(85, 40, 30, 12, 4)
    // Rim
    potSideGraphics.fillStyle(0x5a5a5a)
    potSideGraphics.fillRect(5, 25, 90, 8)
    // Spout
    potSideGraphics.fillStyle(0x4a4a4a)
    potSideGraphics.fillTriangle(5, 25, 0, 35, 10, 35)
    // Highlight
    potSideGraphics.fillStyle(0x6a6a6a)
    potSideGraphics.fillRect(20, 35, 8, 40)
    potSideGraphics.generateTexture('pot_side', 120, 90)
    potSideGraphics.destroy()

    // Empty jar
    const jarGraphics = this.make.graphics({ x: 0, y: 0 })
    // Jar body (transparent effect)
    jarGraphics.fillStyle(0xadd8e6, 0.3)
    jarGraphics.fillRoundedRect(10, 20, 60, 90, { tl: 5, tr: 5, bl: 15, br: 15 })
    // Jar outline
    jarGraphics.lineStyle(3, 0x87ceeb, 0.8)
    jarGraphics.strokeRoundedRect(10, 20, 60, 90, { tl: 5, tr: 5, bl: 15, br: 15 })
    // Jar neck
    jarGraphics.fillStyle(0xadd8e6, 0.3)
    jarGraphics.fillRect(20, 5, 40, 18)
    jarGraphics.lineStyle(3, 0x87ceeb, 0.8)
    jarGraphics.strokeRect(20, 5, 40, 18)
    // Rim
    jarGraphics.fillStyle(0x87ceeb, 0.6)
    jarGraphics.fillRect(18, 2, 44, 6)
    // Highlight
    jarGraphics.fillStyle(0xffffff, 0.4)
    jarGraphics.fillRect(15, 25, 8, 70)
    jarGraphics.generateTexture('jar_empty', 80, 115)
    jarGraphics.destroy()

    // Jar lid
    const lidGraphics = this.make.graphics({ x: 0, y: 0 })
    // Lid top
    lidGraphics.fillStyle(0xb8860b)
    lidGraphics.fillRoundedRect(5, 5, 50, 15, 5)
    // Lid rim
    lidGraphics.fillStyle(0xdaa520)
    lidGraphics.fillRect(0, 15, 60, 8)
    // Highlight
    lidGraphics.fillStyle(0xffd700, 0.5)
    lidGraphics.fillRect(10, 8, 30, 4)
    lidGraphics.generateTexture('jar_lid', 60, 25)
    lidGraphics.destroy()
  }

  private createCharacterPlaceholder(key: string): void {
    // Simple placeholder silhouette for character
    const graphics = this.make.graphics({ x: 0, y: 0 })

    // Body (dress shape)
    graphics.fillStyle(0xff6b6b)
    graphics.fillRoundedRect(40, 80, 120, 150, 20)

    // Head
    graphics.fillStyle(0xffdbac)
    graphics.fillCircle(100, 50, 40)

    // Hair
    graphics.fillStyle(0x8b4513)
    graphics.fillCircle(100, 35, 45)
    graphics.fillRect(55, 35, 90, 30)

    // Chef hat
    graphics.fillStyle(0xffffff)
    graphics.fillRoundedRect(60, 0, 80, 30, 10)
    graphics.fillRect(70, 25, 60, 15)

    // Face placeholder
    graphics.fillStyle(0x333333)
    graphics.fillCircle(85, 50, 5) // left eye
    graphics.fillCircle(115, 50, 5) // right eye
    graphics.fillStyle(0xff6b6b)
    graphics.fillCircle(100, 70, 8) // smile

    graphics.generateTexture(key, 200, 256)
    graphics.destroy()
  }

  create(): void {
    // Small delay to show the completed loading screen
    this.time.delayedCall(500, () => {
      this.scene.start('MainMenuScene')
    })
  }
}
